"use client";

import { type MutableRefObject, useCallback, useEffect, useRef } from "react";
import { trackMonetizationEvent } from "@/lib/analytics/track";

/**
 * Guarantees an affiliate slot's `affiliate_slot_requested` always reaches a
 * terminal outcome (see `src/lib/monetization/accounting-identity.ts`).
 *
 * Incident 1: `affiliate_accounting_identity_balanced` went 18 -> 1479
 * per-mille. `requested` fires when the offer fetch starts, but if the
 * component unmounts (client navigation) or the page is hidden (hard
 * navigation / tab close) BEFORE the fetch resolves, no `impression` /
 * `suppressed` / `empty` ever follows: the resolve handler's `setState` lands
 * on an unmounted component and the terminal effect never runs.
 *
 * Fix: each request owns an AbortController. If the request is still in
 * flight at unmount / `pagehide`, abort it and emit the existing terminal
 * kind `affiliate_slot_empty` with `slot: "unmounted"` as the reason marker
 * (no new event kind, so `requested == impressions + suppressed + empty`
 * keeps holding). A request that resolves normally clears the in-flight
 * marker first, so it can never also emit the unmounted terminal.
 *
 * Incident 2 (2026-09-23, same p0, residual ~71 per-mille the day after
 * Incident 1's fix): an offer can RESOLVE — the fetch settles, `state.status`
 * becomes `"offer"` — and still never reach a terminal. `affiliate_impression`
 * only fires on `AffiliateImpression`'s single-fire 50%-visibility observer
 * (`src/components/affiliate/affiliate-impression.tsx`), which starts a full
 * viewport EARLY (the offer fetch's own `rootMargin: "600px 0px"` head
 * start), so "resolved" and "seen" are genuinely different populations —
 * exactly the gap `AffiliateImpression`'s own doc comment already names.
 * Unmounting (or `pagehide`) before that observer ever fires leaves the
 * request permanently unaccounted, the same shape as Incident 1 one step
 * later in the lifecycle.
 *
 * Fix: `awaitImpression()` arms a second pending flag once a caller knows an
 * offer WILL render (i.e. is not itself already suppressed/dismissed/empty —
 * every caller has that answer already, from the ladder that decides
 * `suppressionReason`). `confirmImpression()` disarms it from the same
 * `onImpression` callback that charges the session's frequency guard. Both
 * flags are mutually exclusive by construction (the impression can only be
 * pending once the in-flight fetch has settled), so `terminate()` checks each
 * independently and can only ever emit ONE terminal per request. The reason
 * marker is `slot: "unseen"` rather than `"unmounted"` so a live query can
 * still tell "we never got an answer" from "we got an answer nobody saw" —
 * both are `affiliate_slot_empty` in the accounting identity's own bucket
 * (empty), not a new event kind.
 *
 * React strict mode (dev) runs mount -> cleanup -> mount. The cleanup emits
 * one terminal for whichever half was pending and resets `ranRef`, so the
 * re-mounted effect issues a fresh request (one more `requested`, one more
 * terminal): the identity stays balanced there too.
 *
 * Incident 3 (2026-09-26, `mobile_sticky` over 24h: requested 452 vs
 * impression 346 + empty 416 [378 `slot: "unseen"`] + suppressed 16 = 778
 * terminals, 1087 per-mille): `awaitImpression()` armed
 * `pendingImpressionRef` unconditionally, including AFTER
 * `confirmImpression()` had already settled the SAME request. Callers
 * (`mobile-sticky-ad.tsx`, `native-ad.tsx`) re-run `awaitImpression()` from an
 * effect keyed on `impressionPending`, so any re-render after the impression
 * confirmed re-armed the guard; the next unmount then emitted a second
 * terminal (`affiliate_slot_empty` / `slot: "unseen"`) for a request that had
 * already terminated once.
 *
 * Fix: `impressionSettledRef` latches true the moment a request's impression
 * is confirmed OR any terminal is emitted for it, and `begin()` clears it for
 * the new request. `awaitImpression()` is now a no-op once that latch is set,
 * so it can never re-arm a request that already reached a terminal outcome.
 *
 * Incident 4 (2026-09-29, identity read ~221 per-mille "too many terminals"
 * after the unattributed-request drop and a seeded-RNG bot were excluded):
 * two more ways one request produced two terminals.
 *  - `pagehide` emitted `empty` / `slot: "unseen"`, but the 50%-visibility
 *    observer stayed armed (the component is not unmounted), so a later
 *    `affiliate_impression` landed after the terminal (8/251 observed).
 *  - The bar remounts `AffiliateImpression` when it hides and re-shows within
 *    a page (footer claim, etc.); the fresh instance fired another impression
 *    with no new `requested`.
 * Fix: `canImpress()` is the observer's gate. It is false once the request
 * settled (impression confirmed OR any terminal emitted), so exactly ONE
 * terminal is emitted per request in either order. A bfcache restore
 * (`pageshow` persisted) re-runs nothing here: the request stays settled, so
 * it neither re-impresses nor emits a second terminal.
 */
export function useSlotRequestTerminal(
	ranRef: MutableRefObject<boolean>,
	surface: string,
	locale: string,
) {
	const inflightRef = useRef<AbortController | null>(null);
	const pendingImpressionRef = useRef(false);
	// True once THIS request's impression is confirmed or a terminal has been
	// emitted for it — see Incident 3 in the doc comment above.
	const impressionSettledRef = useRef(false);
	const latestRef = useRef({ surface, locale });
	latestRef.current = { surface, locale };

	useEffect(() => {
		const terminate = () => {
			const controller = inflightRef.current;
			const impressionPending = pendingImpressionRef.current;
			if (!controller && !impressionPending) return;
			inflightRef.current = null;
			pendingImpressionRef.current = false;
			impressionSettledRef.current = true;
			controller?.abort();
			// Let a strict-mode / bfcache re-mount issue a fresh request.
			ranRef.current = false;
			trackMonetizationEvent({
				eventKind: "affiliate_slot_empty",
				surface: latestRef.current.surface,
				slot: controller ? "unmounted" : "unseen",
				locale: latestRef.current.locale,
			});
		};
		// `pagehide` never fires when a mobile browser backgrounds then kills the
		// tab (iOS Safari, Android Chrome discard), so the request stayed
		// unaccounted: 2026-10-12 24h, mobile_sticky 1,396 requested vs 1,004
		// terminals. `visibilitychange` -> hidden is the last event those
		// browsers reliably deliver. `terminate` is idempotent (one terminal per
		// request), so firing on both is safe.
		const onVisibilityChange = () => {
			if (document.visibilityState === "hidden") terminate();
		};
		window.addEventListener("pagehide", terminate);
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => {
			window.removeEventListener("pagehide", terminate);
			document.removeEventListener("visibilitychange", onVisibilityChange);
			terminate();
		};
	}, [ranRef]);

	/** Start tracking a request; pass the returned signal to `fetch`. */
	const begin = useCallback((): AbortSignal => {
		const controller = new AbortController();
		inflightRef.current = controller;
		impressionSettledRef.current = false;
		return controller.signal;
	}, []);

	/**
	 * Call when the request settles. Returns false when it was already aborted
	 * (the unmounted terminal was emitted) — the caller must then skip
	 * `setState`. Returns true when the caller owns the outcome.
	 */
	const settle = useCallback((signal: AbortSignal): boolean => {
		if (signal.aborted) return false;
		inflightRef.current = null;
		return true;
	}, []);

	/**
	 * Call once a resolved offer is about to render and is not already
	 * accounted for by the caller's own suppression ladder (blocked /
	 * dismissed / empty). Idempotent — safe to call on every render while the
	 * condition holds. A no-op once this request's impression has already
	 * confirmed or a terminal already emitted — see Incident 3 above.
	 */
	const awaitImpression = useCallback(() => {
		if (impressionSettledRef.current) return;
		pendingImpressionRef.current = true;
	}, []);

	/**
	 * Call from the SAME `onImpression` callback that charges the session
	 * frequency guard — the 50%-visibility observer answered, so this request
	 * is no longer at risk of ending unaccounted.
	 */
	const confirmImpression = useCallback(() => {
		pendingImpressionRef.current = false;
		impressionSettledRef.current = true;
	}, []);

	/**
	 * Call when another path has already emitted this request's terminal event
	 * (e.g. the guardrail turned `blocked` while the fetch was still in
	 * flight and `affiliate_slot_suppressed` fired). Without it, an unmount
	 * right after would emit a SECOND terminal for the same request.
	 */
	const markTerminal = useCallback(() => {
		inflightRef.current = null;
		pendingImpressionRef.current = false;
		impressionSettledRef.current = true;
	}, []);

	/**
	 * Gate for the impression observer: false once this request has already
	 * reached a terminal outcome (impression confirmed, or any terminal event
	 * emitted), so a later visibility hit or a remounted observer cannot add a
	 * second terminal. Pass to `AffiliateImpression`'s `canFire`.
	 */
	const canImpress = useCallback(() => !impressionSettledRef.current, []);

	return {
		begin,
		canImpress,
		settle,
		awaitImpression,
		confirmImpression,
		markTerminal,
	};
}
